import { useEffect, useCallback, useState } from "react";

import Lottie from "lottie-react-web";
import { Flex } from "@chakra-ui/layout";

import http from "infra/http";
import { useCurrentAuth } from "./auth.store";
import history from "infra/helpers/history";
import lottieJson from "assets/lotties/splash.json";
import { showError } from "infra/helpers/alerta";
import useLogout from "core/features/auth/logout/useLogout";
import { useAuthState } from "react-firebase-hooks/auth";
import app from "infra/packages/firebase";
import { getAuth } from "firebase/auth";
import { registerMixpanel } from "infra/packages/mixpanel";
import { User } from "./typings/user";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import { registerClarity } from "infra/packages/clarity";

function AuthProvider({ children }: any) {
  const [isLoading, setIsLoading] = useState(true);
  const { update: updateAuth, isLogged } = useCurrentAuth();
  const { tenant: currentTenant, updateCurrentTenant } = useCurrentTenant();

  const { handleLogout } = useLogout();
  const [, loadingAuth] = useAuthState(getAuth(app));

  const init = useCallback(async () => {
    try {
      if (!isLogged) {
        setIsLoading(false);
        handleLogout();
        return;
      }

      const { data } = await http.get<User>("/v1/auth/me");
      const currentTenantUpdatedData = data.tenants.find(
        (t) => currentTenant?.id === t.id
      );

      if (currentTenantUpdatedData) {
        updateCurrentTenant(currentTenantUpdatedData);
      }

      await http.post("/v1/auth/log");

      updateAuth(data);

      registerMixpanel(data);
      registerClarity(data);

      if (window.location.pathname.includes("auth/")) {
        history.push("/");
      }

      setIsLoading(false);
    } catch (e: any) {
      console.log("e", e);
      if (e?.response?.status === 400) {
        showError(e.response?.data?.message).then(() => {
          handleLogout();
        });
      } else if (e?.response?.status === 401) {
        showError(`Você não está autênticado!`).then(() => {
          handleLogout();
        });
      } else if (e?.response?.status === 403) {
        showError(e.response.data.message).then(async () => {
          handleLogout();
        });
      } else {
        setIsLoading(false);
      }
    }
  }, [
    currentTenant?.id,
    handleLogout,
    isLogged,
    updateAuth,
    updateCurrentTenant,
  ]);

  useEffect(() => {
    if (!loadingAuth) {
      if (isLogged) {
        init();
      } else {
        setIsLoading(false);
      }
    }
  }, [init, isLogged, loadingAuth]);

  if (isLoading) {
    return (
      <Flex
        flex={1}
        position="absolute"
        left={0}
        right={0}
        top={0}
        bottom={0}
        justifyContent="center"
        alignItems="center"
      >
        <Lottie
          width={300}
          options={{
            animationData: lottieJson,
          }}
        />
      </Flex>
    );
  }

  return children;
}

export default AuthProvider;
